import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/lib/store';
import { supabase } from '@/lib/supabase';

interface PayPalSubscriptionButtonProps {
  onSuccess: () => void;
  onError: (error: string) => void;
}

export function PayPalSubscriptionButton({ onSuccess, onError }: PayPalSubscriptionButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const user = useAuthStore((state) => state.user);
  const scriptLoaded = useRef(false);

  useEffect(() => {
    // Only load the script once
    if (scriptLoaded.current) return;
    
    const loadPayPalScript = () => {
      const script = document.createElement('script');
      const isLive = import.meta.env.VITE_PAYPAL_ENVIRONMENT === 'live' || import.meta.env.VITE_PAYPAL_ENVIRONMENT === 'production';
      script.src = `https://www.paypal.com/sdk/js?client-id=${import.meta.env.VITE_PAYPAL_CLIENT_ID}&vault=true&intent=subscription`;
      script.setAttribute('data-sdk-integration-source', 'button-factory');
      script.async = true;
      
      script.onload = () => {
        if (window.paypal && containerRef.current) {
          window.paypal.Buttons({
            style: {
              shape: 'rect',
              color: 'gold',
              layout: 'vertical',
              label: 'subscribe'
            },
            createSubscription: function(data: any, actions: any) {
              return actions.subscription.create({
                plan_id: 'P-1PT43253EM856540PM7WRT2I'
              });
            },
            onApprove: async function(data: any) {
              try {
                // Update the seller's subscription type in the database
                if (user) {
                  const { error } = await supabase
                    .from('seller_stores')
                    .update({ 
                      subscription_type: 'premium'
                    })
                    .eq('seller_id', user.id);
                  
                  if (error) {
                    throw error;
                  }
                  
                  // Call the success callback
                  onSuccess();
                }
              } catch (err) {
                console.error('Error updating subscription:', err);
                onError('Failed to update subscription status');
              }
            },
            onError: function(err: any) {
              console.error('PayPal error:', err);
              onError('An error occurred with the PayPal subscription');
            }
          }).render(containerRef.current);
        }
      };
      
      document.body.appendChild(script);
      scriptLoaded.current = true;
    };
    
    loadPayPalScript();
    
    // Cleanup
    return () => {
      const paypalScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
      if (paypalScript) {
        paypalScript.remove();
      }
    };
  }, [user, onSuccess, onError]);

  return <div ref={containerRef} id="paypal-button-container"></div>;
}

// Add this to make TypeScript happy with the PayPal global
declare global {
  interface Window {
    paypal: any;
  }
}